import { useState, useRef, useMemo } from 'react';
import { Upload, FileText, Loader2, Check, X, AlertCircle, Sparkles, TrendingUp, TrendingDown, Copy, ArrowLeft, ArrowRight, ClipboardCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useTransactions } from '@/hooks/useTransactions';
import { toast } from '@/hooks/use-toast';
import { useAIConsent } from '@/hooks/useAIConsent';
import { AIConsentModal } from '@/components/ai/AIConsentModal';
import { useProfile } from '@/hooks/useProfile';
import { usePremiumGate } from '@/components/subscription/PremiumGate';
import { UpgradeModal } from '@/components/subscription/UpgradeModal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { convertToBaseCurrency, roundMoney } from '@/lib/currency-convert';
import type { Currency } from '@/hooks/useTransactions';

interface ParsedTransaction {
  date: string;
  description: string;
  /** Monto tal como aparece en el PDF (moneda `currency`). */
  amount: number;
  /** Moneda de la línea en el extracto. */
  currency: Currency;
  /** Monto convertido a la moneda base del usuario; null hasta aplicar tipo de cambio si hace falta. */
  amountInBase: number | null;
  type: 'income' | 'fixed_expense' | 'variable_expense' | 'saving' | 'investment' | 'debt_payment';
  category: string;
  confidence: number;
  selected: boolean;
  needsReview: boolean;
  isDuplicate?: boolean;
  /** True si esta línea se convirtió desde otra moneda a la base. */
  wasConverted?: boolean;
  /** True si la fecha (año) es sospechosa: futura, sin año o fuera de rango plausible. */
  dateSuspicious?: boolean;
}

interface ImportSummary {
  totalTransactions: number;
  totalIncome: number;
  totalExpenses: number;
  dateRange: string;
}

/** Marca duplicados comparando fecha + monto ya en moneda base (como está guardado en BD). */
function attachDuplicateFlags(
  txs: ParsedTransaction[],
  existing: { date: string; amount: number | string }[],
): ParsedTransaction[] {
  const existingKeys = new Set(
    existing.map((t) => `${t.date}|${Number(t.amount).toFixed(2)}`),
  );
  return txs.map((t) => {
    const amt =
      t.amountInBase != null ? t.amountInBase : roundMoney(Number(t.amount));
    const key = `${t.date}|${Number(amt).toFixed(2)}`;
    const isDuplicate = existingKeys.has(key);
    return {
      ...t,
      isDuplicate,
      selected: !isDuplicate,
    };
  });
}

function ExchangeRatePreview({
  transactions,
  baseCurrency,
  exchangeRateInput,
}: {
  transactions: ParsedTransaction[];
  baseCurrency: Currency;
  exchangeRateInput: string;
}) {
  const raw = exchangeRateInput.replace(',', '.').trim();
  const rate = parseFloat(raw);
  const ok = Number.isFinite(rate) && rate >= 1 && rate <= 2000;
  const pending = transactions.filter((t) => t.amountInBase === null);
  const samples = pending.slice(0, 5);
  if (!ok || samples.length === 0) return null;

  const fmt = (amount: number, curr: Currency) =>
    new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);

  return (
    <div className="rounded-md border bg-background/80 p-3 text-xs space-y-2">
      <p className="font-medium text-foreground">
        Vista previa ({pending.length} línea{pending.length !== 1 ? 's' : ''} a convertir)
      </p>
      <ul className="space-y-2 text-muted-foreground">
        {samples.map((t, i) => {
          const converted = convertToBaseCurrency(t.amount, t.currency, baseCurrency, rate);
          return (
            <li key={i} className="border-b border-border/60 pb-2 last:border-0 last:pb-0">
              <p className="truncate text-foreground/90">{t.description}</p>
              <p className="tabular-nums mt-0.5">
                <span>{fmt(t.amount, t.currency)}</span>
                <span className="mx-1 opacity-60">→</span>
                <span className="font-medium text-foreground">{fmt(converted, baseCurrency)}</span>
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const typeLabels: Record<string, string> = {
  income: 'Ingreso',
  fixed_expense: 'Gasto Fijo',
  variable_expense: 'Gasto Variable',
  saving: 'Ahorro',
  investment: 'Inversión',
  debt_payment: 'Pago Deuda',
};

const typeColors: Record<string, string> = {
  income: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  fixed_expense: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  variable_expense: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  saving: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  investment: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  debt_payment: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

const categoryOptions: Record<string, { value: string; label: string }[]> = {
  income: [
    { value: 'salary', label: 'Salario' },
    { value: 'business', label: 'Negocio' },
    { value: 'freelance', label: 'Freelance' },
    { value: 'other_income', label: 'Otros ingresos' },
  ],
  fixed_expense: [
    { value: 'housing', label: 'Vivienda' },
    { value: 'rent', label: 'Alquiler' },
    { value: 'subscriptions', label: 'Suscripciones' },
    { value: 'insurance', label: 'Seguros' },
    { value: 'utilities', label: 'Servicios' },
  ],
  variable_expense: [
    { value: 'food', label: 'Alimentación' },
    { value: 'transport', label: 'Transporte' },
    { value: 'entertainment', label: 'Entretenimiento' },
    { value: 'health', label: 'Salud' },
    { value: 'education', label: 'Educación' },
    { value: 'shopping', label: 'Compras' },
    { value: 'pets', label: 'Mascotas' },
    { value: 'beauty', label: 'Belleza' },
    { value: 'real_estate', label: 'Bienes Raíces' },
    { value: 'other', label: 'Otros' },
  ],
  saving: [
    { value: 'emergency_fund', label: 'Fondo de emergencia' },
    { value: 'sinking_fund', label: 'Fondo de reserva' },
  ],
  investment: [
    { value: 'investment_etf', label: 'ETFs' },
    { value: 'investment_crypto', label: 'Criptomonedas' },
    { value: 'investment_other', label: 'Otras inversiones' },
  ],
  debt_payment: [
    { value: 'credit_card', label: 'Tarjeta de crédito' },
    { value: 'loan', label: 'Préstamo' },
    { value: 'mortgage', label: 'Hipoteca' },
  ],
};

interface BankStatementImportProps {
  variant?: 'default' | 'compact';
  onImportComplete?: (transactions: { date: string }[]) => void;
  /** When false, this import does not increment the free-tier import counter (used in onboarding). */
  countTowardsLimit?: boolean;
}

export default function BankStatementImport({ variant = 'default', onImportComplete, countTowardsLimit = true }: BankStatementImportProps) {
  const [open, setOpen] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [transactions, setTransactions] = useState<ParsedTransaction[]>([]);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [currency, setCurrency] = useState<Currency>('CRC');
  const [fileName, setFileName] = useState('');
  const [step, setStep] = useState<'exchange_rate' | 'review' | 'confirm'>('review');
  const [exchangeRateInput, setExchangeRateInput] = useState('');
  const [exchangeRateApplied, setExchangeRateApplied] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { session } = useAuth();
  const { profile } = useProfile();
  const { bulkCreateTransactions, transactions: existingTransactions } = useTransactions();
  const { hasConsent, isLoading: consentLoading, grantConsent } = useAIConsent();
  const { allowed: canImport, upgradeOpen, setUpgradeOpen } = usePremiumGate(
    'bank_statement_import',
    true,
  );

  const resetState = () => {
    setTransactions([]);
    setSummary(null);
    setFileName('');
    setIsUploading(false);
    setIsParsing(false);
    setIsSaving(false);
    setStep('review');
    setExchangeRateInput('');
    setExchangeRateApplied(null);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!canImport) {
      setUpgradeOpen(true);
      e.target.value = '';
      return;
    }

    if (file.type !== 'application/pdf') {
      toast({
        title: 'Archivo no válido',
        description: 'Por favor selecciona un archivo PDF.',
        variant: 'destructive',
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'Archivo muy grande',
        description: 'El archivo no puede superar los 10MB.',
        variant: 'destructive',
      });
      return;
    }

    setFileName(file.name);
    setIsUploading(true);
    setIsParsing(true);

    try {
      if (!session) {
        toast({
          title: 'Sesión requerida',
          description: 'Inicia sesión para importar un PDF.',
          variant: 'destructive',
        });
        return;
      }

      // Convert PDF to base64 and send directly to AI for native reading
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64Content = btoa(binary);

      // Send base64 PDF to AI for parsing
      const { data, error } = await supabase.functions.invoke('parse-bank-statement', {
        body: {
          pdfBase64: base64Content,
          fileName: file.name,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw error;
      }

      if (data.error) {
        toast({
          title: 'Error al procesar',
          description: data.error,
          variant: 'destructive',
        });
        resetState();
        return;
      }

      if (!data.transactions || data.transactions.length === 0) {
        toast({
          title: 'Sin transacciones',
          description: 'No se encontraron transacciones en el documento.',
          variant: 'destructive',
        });
        resetState();
        return;
      }

      const baseCur = ((profile?.default_currency ?? data.currency ?? 'CRC') as Currency);

      // Validate and flag transactions with unknown categories or low confidence
      const allValidCategories = Object.values(categoryOptions).flat().map(c => c.value);
      const parsedTransactions: ParsedTransaction[] = data.transactions.map(
        (t: Omit<ParsedTransaction, 'selected' | 'needsReview' | 'currency' | 'amountInBase'> & { currency?: string; dateSuspicious?: boolean }) => {
          const rawCur =
            t.currency === 'USD' || t.currency === 'CRC' ? t.currency : baseCur;
          const validType = Object.keys(categoryOptions).includes(t.type);
          const validCategory = allValidCategories.includes(t.category);
          const lowConfidence = (t.confidence || 0) < 70;
          const dateSuspicious = !!t.dateSuspicious;
          const needsReview = !validType || !validCategory || lowConfidence || dateSuspicious;
          const amountInBase =
            rawCur === baseCur ? roundMoney(Number(t.amount)) : null;
          return {
            ...t,
            currency: rawCur,
            amountInBase,
            type: validType ? t.type : 'variable_expense',
            category: validCategory ? t.category : 'other',
            selected: true,
            needsReview,
            dateSuspicious,
            wasConverted: false,
          };
        },
      );

      const needsFx = parsedTransactions.some((tx) => tx.amountInBase === null);

      setSummary(data.summary);
      setCurrency(baseCur);
      setExchangeRateInput('');
      setExchangeRateApplied(null);

      if (needsFx) {
        setTransactions(parsedTransactions);
        setStep('exchange_rate');
      } else {
        setTransactions(attachDuplicateFlags(parsedTransactions, existingTransactions || []));
        setStep('review');
      }

      toast({
        title: '¡Documento procesado!',
        description: needsFx
          ? `Se encontraron ${parsedTransactions.length} transacciones. Indica el tipo de cambio para convertir a ${baseCur}.`
          : `Se encontraron ${parsedTransactions.length} transacciones.`,
      });
    } catch (error: any) {
      console.error('Error processing PDF:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo procesar el archivo.',
        variant: 'destructive',
      });
      resetState();
    } finally {
      setIsUploading(false);
      setIsParsing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleApplyExchangeRate = () => {
    const raw = exchangeRateInput.replace(',', '.').trim();
    const rate = parseFloat(raw);
    if (!Number.isFinite(rate) || rate < 1 || rate > 2000) {
      toast({
        title: 'Tipo de cambio inválido',
        description: 'Ingresa un número entre 1 y 2000 (CRC por 1 USD).',
        variant: 'destructive',
      });
      return;
    }
    const baseCur = currency;
    setTransactions((prev) => {
      const mapped = prev.map((tx) => {
        if (tx.currency === baseCur) {
          return {
            ...tx,
            amountInBase: roundMoney(tx.amount),
            wasConverted: false,
          };
        }
        const inBase = convertToBaseCurrency(tx.amount, tx.currency, baseCur, rate);
        return {
          ...tx,
          amountInBase: inBase,
          wasConverted: true,
        };
      });
      return attachDuplicateFlags(mapped, existingTransactions || []);
    });
    setExchangeRateApplied(rate);
    setStep('review');
    toast({
      title: 'Tipo de cambio aplicado',
      description:
        baseCur === 'CRC'
          ? `Se usará ${rate} CRC por cada 1 USD para las líneas en dólares.`
          : `Se usará ${rate} CRC por cada 1 USD para convertir colones a dólares.`,
    });
  };

  const toggleTransaction = (index: number) => {
    setTransactions(prev => 
      prev.map((t, i) => i === index ? { ...t, selected: !t.selected } : t)
    );
  };

  const toggleAll = (selected: boolean) => {
    setTransactions(prev => prev.map(t => ({ ...t, selected })));
  };

  const updateTransactionType = (index: number, type: ParsedTransaction['type']) => {
    setTransactions(prev =>
      prev.map((t, i) => 
        i === index 
          ? { ...t, type, category: categoryOptions[type]?.[0]?.value || t.category, needsReview: false }
          : t
      )
    );
  };

  const updateTransactionCategory = (index: number, category: string) => {
    setTransactions(prev =>
      prev.map((t, i) => i === index ? { ...t, category, needsReview: false } : t)
    );
  };

  const updateTransactionDate = (index: number, date: string) => {
    setTransactions(prev =>
      prev.map((t, i) => {
        if (i !== index) return t;
        // Re-evaluar si la fecha corregida sigue siendo sospechosa.
        const today = new Date().toISOString().slice(0, 10);
        const validFormat = /^\d{4}-\d{2}-\d{2}$/.test(date);
        const year = validFormat ? Number(date.slice(0, 4)) : NaN;
        const stillSuspicious = !validFormat || date > today || year < 2000;
        const validType = Object.keys(categoryOptions).includes(t.type);
        const validCategory = Object.values(categoryOptions).flat().map(c => c.value).includes(t.category);
        const lowConfidence = (t.confidence || 0) < 70;
        return {
          ...t,
          date,
          dateSuspicious: stillSuspicious,
          needsReview: !validType || !validCategory || lowConfidence || stillSuspicious,
        };
      })
    );
  };

  const handleImport = async () => {
    const selectedTransactions = transactions.filter(t => t.selected);
    
    if (selectedTransactions.length === 0) {
      toast({
        title: 'Sin selección',
        description: 'Selecciona al menos una transacción para importar.',
        variant: 'destructive',
      });
      return;
    }

    if (selectedTransactions.some((t) => t.amountInBase == null)) {
      toast({
        title: 'Tipo de cambio pendiente',
        description: 'Aplica un tipo de cambio válido antes de importar.',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);

    try {
      const rate = exchangeRateApplied;
      const transactionsToCreate = selectedTransactions.map((t) => {
        const amtBase = t.amountInBase!;
        const converted = !!(t.wasConverted && t.currency !== currency);
        return {
          date: t.date,
          type: t.type,
          category: t.category,
          amount: amtBase,
          notes: t.description,
          payment_method: 'transfer' as const,
          currency,
          original_amount: converted ? roundMoney(t.amount) : null,
          original_currency: converted ? t.currency : null,
          exchange_rate_used: converted && rate != null ? rate : null,
          was_converted: converted,
        };
      });

      await bulkCreateTransactions.mutateAsync(transactionsToCreate);

      if (countTowardsLimit) {
        await supabase.functions.invoke('record-statement-import');
      }

      toast({
        title: '¡Importación exitosa!',
        description: `Se importaron ${selectedTransactions.length} transacciones.`,
      });

      resetState();
      setOpen(false);
      onImportComplete?.(selectedTransactions.map(t => ({ date: t.date })));
    } catch (error: any) {
      console.error('Error importing transactions:', error);
      toast({
        title: 'Error al importar',
        description: error.message || 'No se pudieron guardar las transacciones.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatForeignAmount = (amount: number, curr: Currency) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const selectedCount = transactions.filter(t => t.selected).length;
  const reviewCount = transactions.filter(t => t.needsReview && t.selected).length;
  const duplicateCount = transactions.filter(t => t.isDuplicate).length;
  const suspiciousDateCount = transactions.filter(t => t.dateSuspicious && t.selected).length;
  const totalsReady =
    transactions.length > 0 && transactions.every((t) => t.amountInBase != null);

  const selectedTotals = useMemo(() => {
    let income = 0;
    let expenses = 0;
    transactions.forEach(t => {
      if (!t.selected) return;
      const amt = t.amountInBase;
      if (amt == null) return;
      if (t.type === 'income') income += amt;
      else expenses += amt;
    });
    return { income, expenses, net: income - expenses };
  }, [transactions]);

  // Breakdown for the final confirmation step: grouped by type → category
  const breakdown = useMemo(() => {
    const byType: Record<string, {
      total: number;
      count: number;
      categories: Record<string, { total: number; count: number }>;
    }> = {};
    transactions.forEach(t => {
      if (!t.selected) return;
      const amt = t.amountInBase;
      if (amt == null) return;
      if (!byType[t.type]) byType[t.type] = { total: 0, count: 0, categories: {} };
      byType[t.type].total += amt;
      byType[t.type].count += 1;
      if (!byType[t.type].categories[t.category]) {
        byType[t.type].categories[t.category] = { total: 0, count: 0 };
      }
      byType[t.type].categories[t.category].total += amt;
      byType[t.type].categories[t.category].count += 1;
    });
    // Stable order matching typeLabels
    return Object.keys(typeLabels)
      .filter(k => byType[k])
      .map(k => ({ type: k, ...byType[k] }));
  }, [transactions]);

  const categoryLabel = (type: string, value: string) =>
    categoryOptions[type]?.find(o => o.value === value)?.label || value;

  const handleOpenDialog = () => {
    if (!hasConsent && !consentLoading) {
      setShowConsentModal(true);
      return;
    }
    setOpen(true);
  };

  const handleConsentAccept = async () => {
    const ok = await grantConsent();
    setShowConsentModal(false);
    if (ok) {
      setOpen(true);
    }
  };

  return (
    <>
    <AIConsentModal
      open={showConsentModal}
      onAccept={handleConsentAccept}
      onDecline={() => setShowConsentModal(false)}
    />
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetState(); }}>
      <DialogTrigger asChild>
        {variant === 'compact' ? (
          <Button variant="outline" size="sm" className="gap-2" onClick={(e) => {
            if (!hasConsent && !consentLoading) {
              e.preventDefault();
              setShowConsentModal(true);
            }
          }}>
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Estado de cuenta</span>
          </Button>
        ) : (
          <Button variant="outline" className="gap-2" onClick={(e) => {
            if (!hasConsent && !consentLoading) {
              e.preventDefault();
              setShowConsentModal(true);
            }
          }}>
            <Sparkles className="h-4 w-4" />
            Importar estado de cuenta PDF
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="flex max-h-[90vh] min-h-0 max-w-4xl flex-col gap-0 overflow-hidden p-6">
        <DialogHeader className="shrink-0 space-y-1.5 pb-4">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Importar estado de cuenta
          </DialogTitle>
          <DialogDescription>
            Sube tu estado de cuenta en PDF y la IA clasificará automáticamente las transacciones.
          </DialogDescription>
        </DialogHeader>

        {transactions.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileSelect}
              className="hidden"
              id="pdf-upload"
            />
            
            {isParsing ? (
              <div className="text-center space-y-4">
                <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
                <div>
                  <p className="font-medium">Procesando documento...</p>
                  <p className="text-sm text-muted-foreground">
                    La IA está analizando y clasificando las transacciones
                  </p>
                </div>
              </div>
            ) : (
              <label
                htmlFor="pdf-upload"
                className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
              >
                <Upload className="h-10 w-10 text-muted-foreground mb-4" />
                <p className="text-sm font-medium">Arrastra o haz clic para subir</p>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF de estado de cuenta (máx. 10MB)
                </p>
              </label>
            )}
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
            {step === 'exchange_rate' && (
              <div className="shrink-0 space-y-4 rounded-lg border bg-muted/30 p-4">
                <div className="text-sm">
                  <span className="text-muted-foreground">Archivo:</span>{' '}
                  <span className="font-medium">{fileName}</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Tu moneda base en la app es <strong>{currency}</strong>. El PDF incluye movimientos en otra moneda.
                  Indica el <strong>tipo de cambio actual en colones por cada 1 USD</strong> (por ejemplo 520). Se
                  usará para todas las líneas que deban convertirse en esta importación.
                </p>
                <div className="space-y-2 max-w-xs">
                  <Label htmlFor="exchange-rate-pdf">Tipo de cambio (CRC por 1 USD)</Label>
                  <Input
                    id="exchange-rate-pdf"
                    inputMode="decimal"
                    placeholder="Ej. 520"
                    value={exchangeRateInput}
                    onChange={(e) => setExchangeRateInput(e.target.value)}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  USD → CRC: monto × tipo de cambio. CRC → USD: monto ÷ tipo de cambio.
                </p>
                <ExchangeRatePreview
                  transactions={transactions}
                  baseCurrency={currency}
                  exchangeRateInput={exchangeRateInput}
                />
              </div>
            )}

            {summary && (
              <div className="bg-muted/50 shrink-0 rounded-lg p-4">
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Archivo:</span>{' '}
                    <span className="font-medium">{fileName}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Transacciones:</span>{' '}
                    <span className="font-medium">{summary.totalTransactions}</span>
                  </div>
                  {summary.dateRange && (
                    <div>
                      <span className="text-muted-foreground">Período:</span>{' '}
                      <span className="font-medium">{summary.dateRange}</span>
                    </div>
                  )}
                  {totalsReady && (
                    <>
                      <div className="flex items-center gap-1">
                        <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
                        <span className="text-muted-foreground">Ingresos:</span>{' '}
                        <span className="font-semibold text-emerald-600">
                          {formatAmount(selectedTotals.income)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <TrendingDown className="h-3.5 w-3.5 text-red-600" />
                        <span className="text-muted-foreground">Gastos:</span>{' '}
                        <span className="font-semibold text-red-600">
                          {formatAmount(selectedTotals.expenses)}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Balance:</span>{' '}
                        <span className={`font-semibold ${selectedTotals.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatAmount(selectedTotals.net)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
                {!totalsReady && step === 'exchange_rate' && (
                  <p className="mt-2 text-xs text-amber-800 dark:text-amber-200">
                    Los totales en {currency} aparecerán después de aplicar el tipo de cambio.
                  </p>
                )}
              </div>
            )}

            {duplicateCount > 0 && totalsReady && step === 'review' && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 dark:bg-blue-950/30 dark:border-blue-800 shrink-0">
                <Copy className="h-4 w-4 text-blue-600 shrink-0" />
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <strong>{duplicateCount} posible(s) duplicado(s)</strong> ya existen con la misma fecha y monto. Se desmarcaron automáticamente; revísalos y márcalos solo si realmente quieres importarlos otra vez.
                </p>
              </div>
            )}

            {suspiciousDateCount > 0 && step === 'review' && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-800 shrink-0">
                <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <strong>{suspiciousDateCount} fecha(s)</strong> podrían tener el año equivocado (el PDF no siempre incluye el año en cada línea). Revisa y corrige el año antes de importar.
                </p>
              </div>
            )}

            {reviewCount > 0 && step === 'review' && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-800 shrink-0">
                <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <strong>{reviewCount} transacción(es)</strong> necesitan revisión manual. Ajusta el tipo y categoría antes de importar.
                </p>
              </div>
            )}

            {step === 'review' && (
              <>
              <div className="flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="select-all"
                    checked={selectedCount === transactions.length}
                    onCheckedChange={(checked) => toggleAll(!!checked)}
                  />
                  <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                    Seleccionar todas ({selectedCount}/{transactions.length})
                  </label>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <AlertCircle className="h-3 w-3" />
                  Paso 1 de 2: revisa y ajusta la clasificación
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto border rounded-lg">
              <div className="divide-y">
                {transactions.map((transaction, index) => (
                  <div
                    key={index}
                    className={`p-3 flex items-start gap-3 transition-colors ${
                      transaction.isDuplicate
                        ? 'bg-blue-50/50 dark:bg-blue-950/20 border-l-2 border-l-blue-400'
                        : transaction.needsReview && transaction.selected
                        ? 'bg-amber-50/50 dark:bg-amber-950/20 border-l-2 border-l-amber-400'
                        : transaction.selected ? 'bg-background' : 'bg-muted/30 opacity-60'
                    }`}
                  >
                    <Checkbox
                      checked={transaction.selected}
                      onCheckedChange={() => toggleTransaction(index)}
                      className="mt-1"
                    />
                    
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm break-words">
                            {transaction.description}
                          </p>
                          <p className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                            {transaction.dateSuspicious ? (
                              <span className="inline-flex items-center gap-1">
                                <Input
                                  type="date"
                                  value={/^\d{4}-\d{2}-\d{2}$/.test(transaction.date) ? transaction.date : ''}
                                  max={new Date().toISOString().slice(0, 10)}
                                  onChange={(e) => updateTransactionDate(index, e.target.value)}
                                  className="h-7 w-auto text-xs border-amber-300 focus-visible:ring-amber-400"
                                  title="Revisa el año de esta fecha"
                                />
                                <span className="text-amber-600 dark:text-amber-400" title="Año posiblemente incorrecto">⚠ año</span>
                              </span>
                            ) : (
                              <span>
                              {new Date(transaction.date).toLocaleDateString('es-CR', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}
                              </span>
                            )}
                            <span className="opacity-60">·</span>
                            <span title="Confianza de clasificación de la IA">
                              IA {transaction.confidence ?? 0}%
                            </span>
                          </p>
                        </div>
                        <div className={`text-sm font-semibold whitespace-nowrap ${
                          transaction.type === 'income' ? 'text-green-600' : 'text-foreground'
                        }`}>
                          {transaction.type === 'income' ? '+' : '-'}
                          {formatAmount(transaction.amountInBase ?? transaction.amount)}
                        </div>
                      </div>
                      {transaction.wasConverted && transaction.amountInBase != null && (
                        <p className="text-xs text-muted-foreground pl-0">
                          Original en extracto:{' '}
                          <strong>
                            {formatForeignAmount(transaction.amount, transaction.currency)}
                          </strong>
                          {exchangeRateApplied != null && (
                            <span> · TC usado: {exchangeRateApplied} CRC por 1 USD</span>
                          )}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-2">
                        <Select
                          value={transaction.type}
                          onValueChange={(value) => updateTransactionType(index, value as ParsedTransaction['type'])}
                        >
                          <SelectTrigger className="h-7 w-auto text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(typeLabels).map(([value, label]) => (
                              <SelectItem key={value} value={value} className="text-xs">
                                {label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={transaction.category}
                          onValueChange={(value) => updateTransactionCategory(index, value)}
                        >
                          <SelectTrigger className="h-7 w-auto text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {categoryOptions[transaction.type]?.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value} className="text-xs">
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        {transaction.needsReview && (
                          <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                            ⚠ Revisar
                          </Badge>
                        )}
                        {transaction.isDuplicate && (
                          <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">
                            <Copy className="h-3 w-3 mr-1" /> Duplicado
                          </Badge>
                        )}
                        {transaction.wasConverted && (
                          <Badge variant="secondary" className="text-xs">
                            Convertida a {currency}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
              </>
            )}

            {step === 'confirm' && (
              <div className="flex-1 min-h-0 overflow-y-auto border rounded-lg p-4">
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <ClipboardCheck className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold">Revisión final</h3>
                    <span className="text-sm text-muted-foreground">
                      Paso 2 de 2 — Confirma el desglose antes de guardar
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    Vas a importar <strong className="text-foreground">{selectedCount}</strong>{' '}
                    transacción(es) agrupadas así:
                  </p>

                  {breakdown.length === 0 ? (
                    <div className="text-center py-8 text-sm text-muted-foreground">
                      No hay transacciones seleccionadas. Vuelve al paso anterior.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {breakdown.map(group => (
                        <div
                          key={group.type}
                          className="border rounded-lg overflow-hidden"
                        >
                          <div className="flex items-center justify-between px-3 py-2 bg-muted/50">
                            <div className="flex items-center gap-2">
                              <Badge className={typeColors[group.type]}>
                                {typeLabels[group.type]}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {group.count} transacción(es)
                              </span>
                            </div>
                            <span className={`text-sm font-semibold ${
                              group.type === 'income' ? 'text-emerald-600' : 'text-red-600'
                            }`}>
                              {group.type === 'income' ? '+' : '-'}{formatAmount(group.total)}
                            </span>
                          </div>
                          <div className="divide-y">
                            {Object.entries(group.categories)
                              .sort((a, b) => b[1].total - a[1].total)
                              .map(([cat, info]) => (
                                <div
                                  key={cat}
                                  className="flex items-center justify-between px-4 py-2 text-sm"
                                >
                                  <div className="flex items-center gap-2">
                                    <span>{categoryLabel(group.type, cat)}</span>
                                    <span className="text-xs text-muted-foreground">
                                      ({info.count})
                                    </span>
                                  </div>
                                  <span className="font-medium tabular-nums">
                                    {formatAmount(info.total)}
                                  </span>
                                </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="border-t pt-3 flex flex-col sm:flex-row sm:justify-between gap-2 text-sm">
                    <div className="flex items-center gap-1">
                      <TrendingUp className="h-4 w-4 text-emerald-600" />
                      <span className="text-muted-foreground">Ingresos:</span>
                      <span className="font-semibold text-emerald-600">
                        {formatAmount(selectedTotals.income)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <TrendingDown className="h-4 w-4 text-red-600" />
                      <span className="text-muted-foreground">Gastos:</span>
                      <span className="font-semibold text-red-600">
                        {formatAmount(selectedTotals.expenses)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground">Balance neto:</span>
                      <span className={`font-semibold ${
                        selectedTotals.net >= 0 ? 'text-emerald-600' : 'text-red-600'
                      }`}>
                        {formatAmount(selectedTotals.net)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/40 border text-xs text-muted-foreground">
                    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                      Si algo no se ve bien, vuelve al paso anterior para corregir tipo o categoría de cada transacción.
                      Una vez confirmes, las transacciones se guardarán en tu historial.
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="shrink-0 gap-2 pt-4 sm:gap-0">
          {transactions.length > 0 && (
            <>
              {step === 'exchange_rate' && (
                <>
                  <Button variant="outline" onClick={resetState} disabled={isSaving}>
                    <X className="h-4 w-4 mr-2" />
                    Cancelar
                  </Button>
                  <Button onClick={handleApplyExchangeRate} disabled={isSaving}>
                    Aplicar tipo de cambio y continuar
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </>
              )}
              {step === 'review' && (
                <>
                  <Button variant="outline" onClick={resetState} disabled={isSaving}>
                    <X className="h-4 w-4 mr-2" />
                    Cancelar
                  </Button>
                  <Button
                    onClick={() => setStep('confirm')}
                    disabled={selectedCount === 0 || reviewCount > 0 || !totalsReady}
                  >
                    {reviewCount > 0
                      ? `Revisa ${reviewCount} transacción(es) primero`
                      : !totalsReady
                        ? 'Aplica el tipo de cambio primero'
                        : `Continuar a revisión final`}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </>
              )}
              {step === 'confirm' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setStep('review')}
                    disabled={isSaving}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Volver y corregir
                  </Button>
                  <Button onClick={handleImport} disabled={isSaving || selectedCount === 0}>
                    {isSaving ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Confirmar e importar {selectedCount}
                  </Button>
                </>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <UpgradeModal
      open={upgradeOpen}
      onOpenChange={setUpgradeOpen}
      featureName="Importación de extractos bancarios"
    />
    </>
  );
}
